import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AppointmentScheduler from '../components/AppointmentScheduler';
import RelatedInventory from '../components/RelatedInventory';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';

const { FiTool, FiSettings, FiShield, FiClock, FiCheckCircle, FiCalendar, FiPhone } = FiIcons;

const Service = () => {
  const [appointmentData, setAppointmentData] = useState({
    service: '',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    mileage: '',
    description: '',
  });

  const handleAppointmentSubmit = (e) => {
    e.preventDefault();
    console.log('Service appointment submitted:', appointmentData);
    // Handle appointment submission here
  };

  const handleAppointmentChange = (e) => {
    setAppointmentData({
      ...appointmentData,
      [e.target.name]: e.target.value,
    });
  };

  const services = [
    {
      icon: FiTool,
      title: 'Oil Change',
      description: 'Regular oil changes to keep your engine running smoothly',
      price: 'Starting at $29.99',
      duration: '30 minutes',
    },
    {
      icon: FiSettings,
      title: 'Brake Service',
      description: 'Complete brake inspection, repair, and replacement',
      price: 'Starting at $149.99',
      duration: '2-3 hours',
    },
    {
      icon: FiShield,
      title: 'Multi-Point Inspection',
      description: 'Comprehensive vehicle inspection and safety check',
      price: 'Starting at $99.99',
      duration: '1 hour',
    },
    {
      icon: FiTool,
      title: 'Tire Service',
      description: 'Tire rotation, balancing, and replacement',
      price: 'Starting at $79.99',
      duration: '1-2 hours',
    },
    {
      icon: FiSettings,
      title: 'Engine Diagnostics',
      description: 'Computer diagnostics to identify engine issues',
      price: 'Starting at $129.99',
      duration: '1 hour',
    },
    {
      icon: FiShield,
      title: 'Transmission Service',
      description: 'Transmission fluid change and inspection',
      price: 'Starting at $199.99',
      duration: '2 hours',
    },
  ];

  const benefits = [
    {
      icon: FiCheckCircle,
      title: 'expert Technicians',
      description: 'ASE-expert mechanics with years of North Texas experience',
    },
    {
      icon: FiShield,
      title: 'Quality Parts',
      description: 'OEM and high-quality aftermarket parts',
    },
    {
      icon: FiClock,
      title: 'Quick Turnaround',
      description: 'Most services completed same day',
    },
    {
      icon: FiTool,
      title: 'Latest Equipment',
      description: 'State-of-the-art diagnostic and repair tools',
    },
  ];

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
    '4:00 PM', '5:00 PM',
  ];

  const maintenanceSchedule = [
    { mileage: '5,000', services: ['Oil Change', 'Filter Replacement'] },
    { mileage: '10,000', services: ['Oil Change', 'Tire Rotation', 'Multi-Point Inspection'] },
    { mileage: '15,000', services: ['Oil Change', 'Brake Inspection', 'Fluid Check'] },
    { mileage: '20,000', services: ['Oil Change', 'Air Filter', 'Cabin Filter'] },
    { mileage: '30,000', services: ['Major Service', 'Transmission Service', 'Brake Service'] },
    { mileage: '60,000', services: ['Comprehensive Inspection', 'Timing Belt', 'Spark Plugs'] },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-black text-white"
    >
      <Helmet>
        <title>Auto Service & Repair Paradise, TX | Oil Changes, Brakes, Tires | Paradise-Performance</title>
        <meta name="description" content="Expert auto repair and maintenance in Paradise, TX. Oil changes, brake service, tire rotation, engine repair. Industry Expert technicians. Schedule online or call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/appointment" />
        <meta property="og:title" content="Auto Service & Repair - Paradise-Performance" />
        <meta property="og:description" content="Professional auto repair and maintenance. ASE expert technicians." />
        <meta property="og:url" content="https://paradise-performance.com/appointment" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Paradise-Performance Service Department",
            "description": "Full service auto repair and maintenance",
            "url": "https://paradise-performance.com",
            "telephone": "+18173822316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "08:00",
                "closes": "17:00"
              },
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": "Saturday",
                "opens": "10:00",
                "closes": "14:00",
                "description": "Dealership sales only - service shop closed weekends"
              }
            ],
            "paymentAccepted": "Cash, Credit Card",
            "priceRange": "$$"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <PageHero
        eyebrow="Service & Repair"
        title="Paradise-Performance Service Center"
        subtitle="Expert European vehicle service and auto repairs in Paradise, TX."
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
          <a
            href="#appointment"
            className="inline-flex items-center justify-center rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
          >
            Schedule Service
          </a>
          <a
            href="tel:+18173822316"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-white hover:text-black"
          >
            <SafeIcon icon={FiPhone} className="h-5 w-5" />
            Call Service Dept
          </a>
        </div>
      </PageHero>

      {/* Benefits */}
      <section className="bg-black py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Paradise-Performance"
            title="European Auto Specialists"
            subtitle="At Paradise-Performance, our specialized technicians are experts in European vehicles (BMW, Mercedes, Audi, VW). We use the latest diagnostic tools and equipment to provide top-quality service with transparency and honest pricing."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: index * 0.08, duration: 0.5 }}
                className="group rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 text-center transition-all duration-300 hover:border-red-500/30 hover:bg-white/[0.05]"
              >
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-red-600/20 bg-red-600/10 transition-colors group-hover:bg-red-600/20">
                  <SafeIcon icon={benefit.icon} className="h-7 w-7 text-red-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-white">{benefit.title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-gray-600">
              European vehicle specialists and diesel experts to keep your vehicle running perfectly. From routine maintenance to major repairs, trust Paradise-Performance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg p-6 card-hover"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                  <span>{service.price}</span>
                  <span>{service.duration}</span>
                </div>
                <a
                  href="#appointment"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 rounded-lg font-semibold transition-colors block text-center"
                >
                  Schedule Service
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Maintenance Schedule */}
      {/* <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Maintenance Schedule</h2>
            <p className="text-gray-600">
              Follow our recommended maintenance schedule to keep your vehicle in optimal condition.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-primary-600 text-white p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="font-semibold">Mileage</div>
                  <div className="font-semibold">Recommended Services</div>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {maintenanceSchedule.map((schedule, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div className="font-semibold text-primary-600">
                        {schedule.mileage} miles
                      </div>
                      <div className="text-gray-600">
                        <p>Our specialized technicians provide comprehensive oil change services for European vehicles and diesel trucks with premium quality oils and filters to keep your engine running smoothly.</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Appointment Form */}
      <section id="appointment" className="scroll-mt-24 bg-neutral-950 py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Book Online"
            title="Schedule Your Service"
            subtitle="Book your service appointment online or call us at (817) 382-2316."
          />
          <div className="rounded-3xl border border-white/10 bg-white p-6 text-gray-900 shadow-2xl shadow-black/40 sm:p-8 md:p-10">
            <AppointmentScheduler />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-black py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-red-600/15 via-white/[0.03] to-white/[0.03] p-10 text-center md:p-14">
            <h2 className="mb-4 text-3xl font-bold text-white md:text-4xl">Need Immediate Service?</h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-300">
              Contact our Service Department directly for urgent repairs and same-day service.
            </p>
            <div className="flex justify-center">
              <a
                href="tel:+18173822316"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-red-700"
              >
                <SafeIcon icon={FiPhone} className="h-5 w-5" />
                <span>Call Service Dept</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Inventory Links */}
      <section className="bg-black pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl shadow-black/40 sm:p-8 md:p-10">
            <RelatedInventory limit={4} title="We Also Sell Vehicles — Inspected by Our Techs" />
          </div>
        </div>
      </section>
    </motion.div>

  );
};

export default Service;